import React, { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { beregnAnalyse, kr, SATSER } from "../utils/vognmandBeregner";

// recharts hentes først når brugeren er nået til analysen, så siden kan prerenderes
// og hovedbundtet ikke vokser.
const ScenarieDiagram = lazy(() => import("../components/vognmand/ScenarieDiagram"));
import {
  ArrowLeft, ArrowRight, Calculator, Check, Download, FileText, Mail, Sparkles, TriangleAlert,
} from "lucide-react";

import { Aurora, GOLD, NAVY } from "../components/business/ui";

const TRIN = ["Profil og opstart", "Arbejdstider", "Økonomiske mål", "Bil og investering",
  "Kørsel og kunder", "Personlig analyse"];

const TRANSPORT = [
  ["taxa", "Taxa"], ["flextrafik", "Flextrafik og personbefordring"],
  ["varebil", "Varebil og kurér"], ["lastbil", "Lastbil og godstransport"], ["andet", "Andet"],
];
const OPGAVER = {
  taxa: ["Almindelig taxakørsel", "App- og platformskørsel", "Bestillingskørsel", "Erhvervskunder", "Aften- og weekendkørsel"],
  flextrafik: ["Flextrafik", "Patientbefordring", "Handicapkørsel", "Institutionskørsel", "Anden kontraktkørsel"],
  varebil: ["Pakkedistribution", "Kurérkørsel", "Fast distributionsaftale", "Erhvervstransport", "Ad hoc-opgaver"],
  lastbil: ["Distribution", "Stykgods", "Fast kontraktkørsel", "Regional transport", "Langdistancetransport"],
  andet: ["Kontraktkørsel", "Ad hoc-opgaver", "Erhvervstransport"],
};
const DAGE = [["man", "Man"], ["tir", "Tir"], ["ons", "Ons"], ["tor", "Tor"], ["fre", "Fre"], ["lør", "Lør"], ["søn", "Søn"]];

const START = {
  transporttype: "flextrafik", status: "undersøger", område: "", startdato: "", virksomhedsform: "ikke_afklaret",
  arbejdsdage: ["man", "tir", "ons", "tor", "fre"], timerOmUgen: 40, tidspunkter: ["dag"],
  weekendvillig: "nogle", fastEllerFleksibel: "fleksibel", ferieuger: 5, belægning: 70,
  indkomstmål: 35000, målErEfterSkat: false, prioritet: "stabil", andenIndkomst: 0,
  opsparingOmMåneden: 0, sikkerhedsbeløb: 25000, trækprocent: 38, månedsfradrag: 4800,
  harBil: "nej", anskaffelse: "leasing", antalBiler: 1, kategori: "personbil", drivmiddel: "diesel",
  kmOmMåneden: 4000, leasing: 8000, forsikring: 2000, ejerafgift: 500, energiPrKm: 1.2, energi: 0,
  service: 1500, administration: 1500, abonnementer: 500, øvrige: 500,
  engangsinvestering: 25000, opstartskapital: 50000, finansieringsbehov: "nej",
  harAftaler: "nej", opgaver: [], indtægtsmodel: "time", timepris: 420, dagspris: 3200, kmPris: 14,
  kontraktindtægt: 55000, fakturerbareTimer: 0, fakturerbareDage: 0, fakturerbareKm: 0,
  provision: 0, kørerSelv: "ja", antalChauffører: 0, chaufførLønOmMåneden: 0, betalingsdage: 30,
  bindendeAftale: "nej",
};

const Card = ({ title, children, className = "", testid }) => (
  <section data-testid={testid}
    className={`rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(10,25,47,0.04)] ${className}`}>
    {title ? (
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-4">{title}</h3>
    ) : null}
    {children}
  </section>
);

const Felt = ({ label, hint, children }) => (
  <label className="block mb-4">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
    {children}
    {hint ? <span className="block text-xs text-slate-400 mt-1">{hint}</span> : null}
  </label>
);

const Valg = ({ options, value, onChange, name, multi = false }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([v, t]) => {
      const aktiv = multi ? (value || []).includes(v) : value === v;
      return (
        <button key={v} type="button" data-testid={`valg-${name}-${v}`}
          onClick={() => onChange(multi ? (aktiv ? value.filter((x) => x !== v) : [...(value || []), v]) : v)}
          className="px-3.5 py-2 rounded-xl text-sm font-medium border transition-colors"
          style={aktiv
            ? { backgroundColor: NAVY, color: "#fff", borderColor: NAVY }
            : { backgroundColor: "#fff", color: "#334155", borderColor: "#E2E8F0" }}>
          {t}
        </button>
      );
    })}
  </div>
);

const Tal = ({ value, onChange, suffix = "kr.", step = 100, testid }) => (
  <div className="flex items-center rounded-xl border border-slate-200 overflow-hidden bg-white focus-within:border-slate-400">
    <input type="number" inputMode="decimal" value={value} step={step} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      className="w-full px-3 py-2.5 text-sm tabular-nums outline-none" />
    <span className="px-3 text-xs text-slate-400 shrink-0">{suffix}</span>
  </div>
);

const Kpi = ({ label, value, hint, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className="mt-1 text-xl sm:text-2xl font-bold tabular-nums leading-tight whitespace-nowrap"
      style={{ color: accent || NAVY }}>{value}</p>
    {hint ? <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p> : null}
  </div>
);

export default function VognmandOnboarding() {
  const [trin, setTrin] = useState(0);
  const [s, setS] = useState(START);
  const [kontakt, setKontakt] = useState({
    navn: "", telefon: "", email: "", virksomhed: "", cvr: "",
    samtykke_markedsforing: false, oensker_gennemgang: false, send_kopi: false, felde: "",
  });
  const [gemmer, setGemmer] = useState(false);
  const [gemt, setGemt] = useState(false);

  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const a = useMemo(() => beregnAnalyse(s), [s]);
  const opgaver = OPGAVER[s.transporttype] || OPGAVER.andet;

  const vurdering = {
    opnåeligt: { txt: "Dit indkomstmål ser opnåeligt ud under dine egne forudsætninger.", bg: "#F0FDF4", fg: "#166534" },
    tæt_på: { txt: "Du er tæt på målet. Små ændringer i pris eller fakturerbare timer afgør det.", bg: "#FFFBEB", fg: "#92400E" },
    kræver_ændringer: { txt: "Målet kræver ændrede forudsætninger: højere pris, flere fakturerbare timer eller lavere omkostninger.", bg: "#FEF2F2", fg: "#991B1B" },
    mangler_data: { txt: "Der mangler oplysninger til en pålidelig vurdering. Udfyld timer, priser og omkostninger.", bg: "#F8FAFC", fg: "#475569" },
  }[a.vurdering];

  const gem = async (medPdf = false) => {
    if (!kontakt.navn || !kontakt.telefon || !kontakt.email) {
      toast.error("Udfyld navn, telefon og e-mail.");
      return;
    }
    setGemmer(true);
    try {
      const krop = { ...kontakt, svar: s, analyse: a };
      await axios.post(`${API}/vognmand-onboarding/plan`, krop);
      setGemt(true);
      toast.success("Din vognmandsplan er gemt. Vi kontakter dig, hvis du har bedt om en gennemgang.");
      if (medPdf) await hentPdf();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke gemme planen. Prøv igen.");
    } finally { setGemmer(false); }
  };

  const hentPdf = async () => {
    setGemmer(true);
    try {
      const r = await axios.post(`${API}/vognmand-onboarding/pdf`,
        { ...kontakt, navn: kontakt.navn || "Vognmandsplan", telefon: kontakt.telefon || "00000000",
          email: kontakt.email || "ikke@oplyst.dk", svar: s, analyse: a },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = "min-vognmandsplan.pdf";
      el.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF'en kunne ikke dannes."); }
    finally { setGemmer(false); }
  };

  return (
    <div className="votra-biz min-h-screen bg-[#F6F7F9]" data-testid="vognmand-onboarding">
      <header className="relative overflow-hidden text-white" style={{ backgroundColor: NAVY }} data-testid="onboarding-hero">
        <Aurora grid />
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative py-14 sm:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-8" aria-label="Brødkrumme">
            <Link to="/business" className="hover:text-slate-300 transition-colors">Votra Business</Link>
            <ArrowRight className="h-3 w-3" />
            <span>Vognmand Onboarding</span>
          </nav>
          <p className="votra-hero-rise votra-hero-rise--1 text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: GOLD }}>
            Fra drøm til drift
          </p>
          <h1 className="votra-hero-rise votra-hero-rise--2 font-display font-medium leading-[1.12] text-4xl sm:text-5xl lg:text-[3.5rem] max-w-4xl mb-7"
            data-testid="onboarding-title">
            Lav din vognmandsplan,{" "}
            <span className="italic votra-shimmer-text inline-block pb-1">før du kører første tur.</span>
          </h1>
          <p className="votra-hero-rise votra-hero-rise--3 text-base md:text-lg leading-relaxed text-slate-300 max-w-2xl">
            Hvad skal du arbejde for at nå dit mål? Du indtaster dine egne tal, og beregneren viser
            arbejdstimer, omkostninger, break-even og hvad der realistisk er tilbage til dig selv.
          </p>
          <div className="votra-hero-rise votra-hero-rise--4 mt-10 flex flex-wrap items-center gap-3 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur">
              <Calculator className="h-4 w-4" style={{ color: GOLD }} /> Alle beløb ekskl. moms
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur">
              <Sparkles className="h-4 w-4" style={{ color: GOLD }} /> Dine forudsætninger kan rettes hele vejen
            </span>
          </div>
        </div>
      </header>

      <nav className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <ol className="flex gap-1 overflow-x-auto py-3 text-xs" data-testid="trin-indikator">
            {TRIN.map((t, i) => (
              <li key={t} className="shrink-0">
                <button onClick={() => setTrin(i)} data-testid={`trin-knap-${i + 1}`}
                  className="px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors"
                  style={i === trin
                    ? { backgroundColor: NAVY, color: "#fff" }
                    : { color: i < trin ? GOLD : "#94A3B8" }}>
                  {i + 1}. {t}
                </button>
              </li>
            ))}
          </ol>
          <div className="h-1 rounded-full bg-slate-100 mb-3">
            <div className="h-1 rounded-full transition-all" style={{ width: `${((trin + 1) / 6) * 100}%`, backgroundColor: GOLD }} />
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          Trin {trin + 1} af 6
        </p>
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mt-1 mb-6">{TRIN[trin]}</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          <div className="lg:col-span-2 space-y-5">
            {trin === 0 && (
              <Card title="Vognmandens profil" testid="trin-1">
                <Felt label="Hvilken type vognmand ønsker du at blive?">
                  <Valg name="transport" options={TRANSPORT} value={s.transporttype} onChange={set("transporttype")} />
                </Felt>
                <Felt label="Hvor langt er du i processen?">
                  <Valg name="status" value={s.status} onChange={set("status")} options={[
                    ["undersøger", "Undersøger mulighederne"], ["uddannelse", "Under uddannelse"],
                    ["uddannet", "Uddannelse gennemført"], ["behandling", "Tilladelse under behandling"],
                    ["godkendt", "Allerede godkendt vognmand"]]} />
                </Felt>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Hvilket geografisk område ønsker du at arbejde i?">
                    <input value={s.område} onChange={(e) => set("område")(e.target.value)} data-testid="input-område"
                      placeholder="fx Storkøbenhavn eller Vestsjælland"
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
                  </Felt>
                  <Felt label="Hvornår ønsker du at starte?">
                    <input type="month" value={s.startdato} onChange={(e) => set("startdato")(e.target.value)}
                      data-testid="input-startdato"
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
                  </Felt>
                </div>
                <Felt label="Virksomhedsform"
                  hint="Formen afgør, om dit overskud beskattes som løn eller som selskabets overskud.">
                  <Valg name="form" value={s.virksomhedsform} onChange={set("virksomhedsform")} options={[
                    ["aps", "ApS"], ["personlig", "Personligt ejet"], ["ikke_afklaret", "Ikke afklaret endnu"]]} />
                </Felt>
              </Card>
            )}

            {trin === 1 && (
              <Card title="Arbejdstider og arbejdsmønster" testid="trin-2">
                <Felt label="Hvilke dage ønsker du at arbejde?">
                  <Valg name="dage" multi options={DAGE} value={s.arbejdsdage} onChange={set("arbejdsdage")} />
                </Felt>
                <Felt label={`Arbejdstimer pr. uge: ${s.timerOmUgen}`}>
                  <input type="range" min="10" max="70" value={s.timerOmUgen} data-testid="slider-timer"
                    onChange={(e) => set("timerOmUgen")(Number(e.target.value))} className="w-full accent-[#C8A14A]" />
                </Felt>
                <Felt label="Hvilke tidspunkter passer dig bedst?">
                  <Valg name="tid" multi value={s.tidspunkter} onChange={set("tidspunkter")} options={[
                    ["morgen", "Morgen"], ["dag", "Dag"], ["eftermiddag", "Eftermiddag"],
                    ["aften", "Aften"], ["nat", "Nat"]]} />
                </Felt>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Weekend og helligdage">
                    <Valg name="weekend" value={s.weekendvillig} onChange={set("weekendvillig")} options={[
                      ["ja", "Ja, gerne"], ["nogle", "Nogle gange"], ["nej", "Nej"]]} />
                  </Felt>
                  <Felt label="Faste eller fleksible arbejdstider">
                    <Valg name="fast" value={s.fastEllerFleksibel} onChange={set("fastEllerFleksibel")} options={[
                      ["fast", "Faste"], ["fleksibel", "Fleksible"]]} />
                  </Felt>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Ferieuger pr. år"><Tal value={s.ferieuger} onChange={set("ferieuger")} suffix="uger" step={1} testid="input-ferie" /></Felt>
                  <Felt label={`Hvor stor del af tiden er fakturerbar? ${s.belægning} %`}
                    hint="Klargøring, tomkørsel og pauser giver ingen omsætning.">
                    <input type="range" min="30" max="95" value={s.belægning} data-testid="slider-belægning"
                      onChange={(e) => set("belægning")(Number(e.target.value))} className="w-full accent-[#C8A14A]" />
                  </Felt>
                </div>
              </Card>
            )}

            {trin === 2 && (
              <Card title="Personlig indkomst" testid="trin-3">
                <Felt label="Hvor meget ønsker du personligt at tjene pr. måned?">
                  <Tal value={s.indkomstmål} onChange={set("indkomstmål")} step={1000} testid="input-indkomstmål" />
                  <div className="mt-2">
                    <Valg name="skat" value={s.målErEfterSkat ? "efter" : "før"}
                      onChange={(v) => set("målErEfterSkat")(v === "efter")}
                      options={[["før", "Før skat"], ["efter", "Efter skat, udbetalt"]]} />
                  </div>
                </Felt>
                <div className="rounded-xl p-3.5 mb-4 text-xs leading-relaxed" style={{ backgroundColor: "#FFFBEB", color: "#92400E" }}>
                  Omsætning er ikke det samme som din løn. Et ønske om {kr(s.indkomstmål)} udbetalt kræver
                  både dækning af driftsomkostninger og skat, og beregneren viser dig forskellen.
                </div>
                <Felt label="Hvad er vigtigst for dig?">
                  <Valg name="prioritet" value={s.prioritet} onChange={set("prioritet")} options={[
                    ["stabil", "Stabil indkomst"], ["høj", "Højest mulig indtjening"],
                    ["fleksibilitet", "Fleksibilitet"], ["balance", "Work-life balance"],
                    ["vækst", "Opbygge en større virksomhed"]]} />
                </Felt>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Anden personlig indkomst pr. måned"><Tal value={s.andenIndkomst} onChange={set("andenIndkomst")} testid="input-anden-indkomst" /></Felt>
                  <Felt label="Ønsket opsparing i virksomheden pr. måned"><Tal value={s.opsparingOmMåneden} onChange={set("opsparingOmMåneden")} /></Felt>
                  <Felt label="Økonomisk sikkerhedsbeløb du vil have til rådighed"><Tal value={s.sikkerhedsbeløb} onChange={set("sikkerhedsbeløb")} step={5000} /></Felt>
                  <Felt label="Din trækprocent" hint="Står på din forskudsopgørelse. Bruges kun til et estimat.">
                    <Tal value={s.trækprocent} onChange={set("trækprocent")} suffix="%" step={1} />
                  </Felt>
                </div>
              </Card>
            )}

            {trin === 3 && (
              <Card title="Køretøj og investering" testid="trin-4">
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Har du allerede en bil?">
                    <Valg name="harbil" value={s.harBil} onChange={set("harBil")} options={[["ja", "Ja"], ["nej", "Nej"]]} />
                  </Felt>
                  <Felt label="Køb, finansiering eller leasing?">
                    <Valg name="anskaf" value={s.anskaffelse} onChange={set("anskaffelse")} options={[
                      ["køb", "Køb"], ["finansiering", "Finansiering"], ["leasing", "Leasing"]]} />
                  </Felt>
                  <Felt label="Antal biler ved start"><Tal value={s.antalBiler} onChange={set("antalBiler")} suffix="biler" step={1} testid="input-antal-biler" /></Felt>
                  <Felt label="Kilometer pr. måned"><Tal value={s.kmOmMåneden} onChange={set("kmOmMåneden")} suffix="km" step={500} /></Felt>
                  <Felt label="Køretøjskategori">
                    <Valg name="kat" value={s.kategori} onChange={set("kategori")} options={[
                      ["personbil", "Personbil"], ["liftvogn", "Liftvogn"], ["varebil", "Varebil"], ["lastbil", "Lastbil"]]} />
                  </Felt>
                  <Felt label="Drivmiddel">
                    <Valg name="drivmiddel" value={s.drivmiddel} onChange={set("drivmiddel")} options={[
                      ["diesel", "Diesel"], ["benzin", "Benzin"], ["el", "El"], ["hybrid", "Hybrid"]]} />
                  </Felt>
                </div>
                <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mt-6 mb-3">
                  Løbende omkostninger pr. bil, pr. måned
                </h4>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Leasing eller finansiering"><Tal value={s.leasing} onChange={set("leasing")} testid="input-leasing" /></Felt>
                  <Felt label="Forsikring"><Tal value={s.forsikring} onChange={set("forsikring")} /></Felt>
                  <Felt label="Vægtafgift eller ejerafgift"><Tal value={s.ejerafgift} onChange={set("ejerafgift")} /></Felt>
                  <Felt label="Energi pr. kilometer" hint="Brændstof eller strøm. Ganges med dine kilometer.">
                    <Tal value={s.energiPrKm} onChange={set("energiPrKm")} suffix="kr./km" step={0.1} />
                  </Felt>
                  <Felt label="Service, dæk og reparationer"><Tal value={s.service} onChange={set("service")} /></Felt>
                  <Felt label="Bogføring og administration"><Tal value={s.administration} onChange={set("administration")} /></Felt>
                  <Felt label="Abonnementer og kørselsplatforme"><Tal value={s.abonnementer} onChange={set("abonnementer")} /></Felt>
                  <Felt label="Øvrige driftsomkostninger"><Tal value={s.øvrige} onChange={set("øvrige")} /></Felt>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-5 mt-2">
                  <Felt label="Engangsinvestering ved opstart" hint="Udbetaling, udstyr, klargøring og gebyrer.">
                    <Tal value={s.engangsinvestering} onChange={set("engangsinvestering")} step={5000} />
                  </Felt>
                  <Felt label="Din egen opstartskapital"><Tal value={s.opstartskapital} onChange={set("opstartskapital")} step={5000} /></Felt>
                </div>
              </Card>
            )}

            {trin === 4 && (
              <Card title="Kørselsaftaler og forretningsmodel" testid="trin-5">
                <Felt label="Har du allerede kunder eller kørselsaftaler?">
                  <Valg name="aftaler" value={s.harAftaler} onChange={set("harAftaler")} options={[
                    ["ja", "Ja"], ["dialog", "I dialog"], ["nej", "Nej, ikke endnu"]]} />
                </Felt>
                <Felt label="Hvilke opgaver ønsker du at udføre?">
                  <Valg name="opgaver" multi value={s.opgaver} onChange={set("opgaver")}
                    options={opgaver.map((o) => [o, o])} />
                </Felt>
                <Felt label="Indtægtsmodel" hint="Timepris, dagspris, kilometerpris og kontrakt blandes aldrig sammen i beregningen.">
                  <Valg name="model" value={s.indtægtsmodel} onChange={set("indtægtsmodel")} options={[
                    ["time", "Timepris"], ["dag", "Dagspris"], ["km", "Kilometerpris"], ["kontrakt", "Fast kontrakt"]]} />
                </Felt>
                <div className="grid sm:grid-cols-2 gap-x-5">
                  {s.indtægtsmodel === "time" && (<>
                    <Felt label="Din timepris"><Tal value={s.timepris} onChange={set("timepris")} step={10} testid="input-timepris" /></Felt>
                    <Felt label="Fakturerbare timer pr. måned" hint={`0 bruger beregningen: ${a.arbejdstid.fakturerbareTimerOmMåneden} timer.`}>
                      <Tal value={s.fakturerbareTimer} onChange={set("fakturerbareTimer")} suffix="timer" step={5} />
                    </Felt></>)}
                  {s.indtægtsmodel === "dag" && (<>
                    <Felt label="Din dagspris"><Tal value={s.dagspris} onChange={set("dagspris")} step={100} /></Felt>
                    <Felt label="Kørselsdage pr. måned"><Tal value={s.fakturerbareDage} onChange={set("fakturerbareDage")} suffix="dage" step={1} /></Felt></>)}
                  {s.indtægtsmodel === "km" && (<>
                    <Felt label="Din kilometerpris"><Tal value={s.kmPris} onChange={set("kmPris")} suffix="kr./km" step={0.5} /></Felt>
                    <Felt label="Fakturerbare kilometer pr. måned"><Tal value={s.fakturerbareKm} onChange={set("fakturerbareKm")} suffix="km" step={500} /></Felt></>)}
                  {s.indtægtsmodel === "kontrakt" && (
                    <Felt label="Kontraktindtægt pr. måned"><Tal value={s.kontraktindtægt} onChange={set("kontraktindtægt")} step={1000} /></Felt>)}
                  <Felt label="Provision til platform eller kørselskontor"><Tal value={s.provision} onChange={set("provision")} suffix="%" step={1} /></Felt>
                  <Felt label="Betalingsfrist på dine aftaler" hint="Lang frist binder likviditet.">
                    <Tal value={s.betalingsdage} onChange={set("betalingsdage")} suffix="dage" step={5} />
                  </Felt>
                  <Felt label="Kører du selv?">
                    <Valg name="selv" value={s.kørerSelv} onChange={set("kørerSelv")} options={[["ja", "Ja"], ["delvis", "Delvis"], ["nej", "Nej"]]} />
                  </Felt>
                  <Felt label="Antal ansatte chauffører"><Tal value={s.antalChauffører} onChange={set("antalChauffører")} suffix="stk." step={1} /></Felt>
                  {s.antalChauffører > 0 && (
                    <Felt label="Løn pr. chauffør pr. måned" hint="Feriepenge, ATP og pension lægges automatisk oveni.">
                      <Tal value={s.chaufførLønOmMåneden} onChange={set("chaufførLønOmMåneden")} step={1000} />
                    </Felt>)}
                  <Felt label="Foreligger der en bindende aftale?">
                    <Valg name="bindende" value={s.bindendeAftale} onChange={set("bindendeAftale")} options={[["ja", "Ja"], ["nej", "Nej"]]} />
                  </Felt>
                </div>
              </Card>
            )}

            {trin === 5 && (<>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-kpier">
                <Kpi label="Indkomstmål" value={kr(a.målFørSkat)} hint="før personlig skat" />
                <Kpi label="Driftsudgifter" value={kr(a.omkostninger.total)} hint="pr. måned, dine tal" />
                <Kpi label="Omsætningsbehov" value={kr(a.behov.omsætningsbehov)} hint="for at nå målet" accent={GOLD} />
                <Kpi label="Fakturerbare timer" value={`${a.arbejdstid.fakturerbareTimerOmMåneden}`} hint={`af ${a.arbejdstid.timerOmMåneden} timer pr. måned`} />
              </div>

              <Card title="Arbejdstidsanalyse" testid="analyse-arbejdstid">
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  {[["Arbejdsdage pr. måned", a.arbejdstid.dageOmMåneden],
                    ["Arbejdstimer pr. måned", a.arbejdstid.timerOmMåneden],
                    ["Fakturerbare timer", a.arbejdstid.fakturerbareTimerOmMåneden],
                    ["Arbejdsuger pr. år", a.arbejdstid.arbejdsuger]].map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-xs text-slate-400">{k}</dt>
                        <dd className="text-lg font-semibold tabular-nums" style={{ color: NAVY }}>{v}</dd>
                      </div>))}
                </dl>
              </Card>

              <Card title="Økonomisk analyse" testid="analyse-økonomi">
                <table className="w-full text-sm">
                  <tbody>
                    {[["Månedlig omsætning", kr(a.omsætning.brutto), a.omsætning.grundlag],
                      ["Provision", `- ${kr(a.omsætning.provision)}`, "til platform eller kørselskontor"],
                      ["Driftsomkostninger", `- ${kr(a.omkostninger.total)}`, `${a.omkostninger.antalBiler} bil(er)`],
                      ["Resultat før ejeraflønning", kr(a.resultatFørEjer), ""],
                      ["Ejeraflønning", kr(a.ejer.ejerløn), a.ejer.form],
                      ["Estimeret udbetalt til dig", kr(a.ejer.efterSkat), "estimat, afhænger af din skat"],
                      ["Årligt omsætningspotentiale", kr(a.årligOmsætning), ""],
                      ["Likviditetsbehov", kr(a.likviditetsbehov), `betalingsfrist ${s.betalingsdage} dage`],
                      ["Finansieringsbehov ved opstart", kr(a.finansieringsbehov), "engangsinvestering minus egen kapital"],
                    ].map(([k, v, h]) => (
                      <tr key={k} className="border-b border-slate-100 last:border-0">
                        <td className="py-2.5 pr-3 text-slate-600">{k}
                          {h ? <span className="block text-[11px] text-slate-400">{h}</span> : null}</td>
                        <td className="py-2.5 text-right font-semibold tabular-nums whitespace-nowrap" style={{ color: NAVY }}>{v}</td>
                      </tr>))}
                  </tbody>
                </table>
              </Card>

              <Card title="Break-even" testid="analyse-breakeven">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  {[["Omsætning der dækker driften", kr(a.behov.breakEvenDrift)],
                    ["Omsætning der også når dit mål", kr(a.behov.omsætningsbehov)],
                    ["Nødvendig omsætning pr. arbejdsdag", kr(a.behov.prDag)],
                    ["Nødvendig omsætning pr. fakturerbar time", kr(a.behov.prTime)],
                    ["Fakturerbare timer nødvendige", `${a.behov.timerNødvendige} timer`]].map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-slate-50 px-4 py-3">
                        <p className="text-xs text-slate-500">{k}</p>
                        <p className="text-lg font-semibold tabular-nums whitespace-nowrap" style={{ color: NAVY }}>{v}</p>
                      </div>))}
                </div>
              </Card>

              <Card title="Scenarier, forventet omsætning" testid="analyse-scenarier">
                <Suspense fallback={<div className="h-56" />}>
                  <ScenarieDiagram scenarier={a.scenarier} />
                </Suspense>
                <p className="text-xs text-slate-400 mt-2">
                  Konservativt er 15 % under din forventning, optimistisk er 15 % over. Til dig før skat:{" "}
                  {a.scenarier.map((x) => `${x.navn} ${kr(x.ejerFørSkat)}`).join(" · ")}
                </p>
              </Card>

              <Card title="Målopfyldelse" testid="analyse-mål">
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-xs text-slate-400">Dit mål før skat</p>
                    <p className="text-xl font-bold tabular-nums whitespace-nowrap" style={{ color: NAVY }}>{kr(a.målFørSkat)}</p></div>
                  <div><p className="text-xs text-slate-400">Beregnet til dig</p>
                    <p className="text-xl font-bold tabular-nums whitespace-nowrap" style={{ color: NAVY }}>{kr(a.ejer.førSkat)}</p></div>
                  <div><p className="text-xs text-slate-400">Forskel</p>
                    <p className="text-xl font-bold tabular-nums whitespace-nowrap"
                      style={{ color: a.forskel >= 0 ? "#166534" : "#991B1B" }}>
                      {a.forskel >= 0 ? "+" : ""}{kr(a.forskel)}</p></div>
                </div>
                <p className="mt-4 rounded-xl px-4 py-3 text-sm" data-testid="vurdering"
                  style={{ backgroundColor: vurdering.bg, color: vurdering.fg }}>{vurdering.txt}</p>
              </Card>

              <Card title="Kontakt og din plan" testid="lead-form">
                <div className="grid sm:grid-cols-2 gap-x-5">
                  <Felt label="Fulde navn">
                    <input value={kontakt.navn} data-testid="input-navn" onChange={(e) => setKontakt({ ...kontakt, navn: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></Felt>
                  <Felt label="Telefonnummer">
                    <input value={kontakt.telefon} data-testid="input-telefon" onChange={(e) => setKontakt({ ...kontakt, telefon: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></Felt>
                  <Felt label="E-mail">
                    <input type="email" value={kontakt.email} data-testid="input-email" onChange={(e) => setKontakt({ ...kontakt, email: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></Felt>
                  <Felt label="Virksomhed (hvis du har en)">
                    <input value={kontakt.virksomhed} onChange={(e) => setKontakt({ ...kontakt, virksomhed: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></Felt>
                  <Felt label="CVR (hvis du har et)">
                    <input value={kontakt.cvr} onChange={(e) => setKontakt({ ...kontakt, cvr: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></Felt>
                </div>
                <input tabIndex={-1} autoComplete="off" value={kontakt.felde} aria-hidden="true"
                  onChange={(e) => setKontakt({ ...kontakt, felde: e.target.value })}
                  className="hidden" />
                <div className="space-y-2 text-sm text-slate-600">
                  {[["oensker_gennemgang", "Jeg vil gerne have en personlig gennemgang med Votra Business"],
                    ["send_kopi", "Send mig en kopi af min plan på e-mail"],
                    ["samtykke_markedsforing", "Ja tak til nyheder og tilbud fra Votra (frivilligt)"]].map(([k, t]) => (
                      <label key={k} className="flex items-start gap-2.5">
                        <input type="checkbox" checked={kontakt[k]} data-testid={`check-${k}`}
                          onChange={(e) => setKontakt({ ...kontakt, [k]: e.target.checked })} className="mt-1" />
                        <span>{t}</span>
                      </label>))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Vi bruger dine oplysninger til at vende tilbage om din vognmandsplan. Læs mere i{" "}
                  <Link to="/persondata" className="underline">persondata og cookies</Link>. Samtykke til
                  markedsføring er frivilligt og ikke nødvendigt for beregningen.
                </p>
                <div className="flex flex-wrap gap-2.5 mt-5">
                  <button onClick={() => gem(false)} disabled={gemmer} data-testid="gem-plan"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                    style={{ backgroundColor: NAVY }}>
                    <Check className="h-4 w-4" /> {gemt ? "Gemt" : "Gem min vognmandsplan"}
                  </button>
                  <button onClick={hentPdf} disabled={gemmer} data-testid="hent-pdf"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: GOLD, color: NAVY }}>
                    <Download className="h-4 w-4" /> Download PDF
                  </button>
                  <button onClick={() => { setKontakt({ ...kontakt, oensker_gennemgang: true }); gem(false); }}
                    disabled={gemmer} data-testid="book-gennemgang"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border border-slate-300 text-slate-700 disabled:opacity-50">
                    <Mail className="h-4 w-4" /> Få en gennemgang hos Votra
                  </button>
                </div>
              </Card>
            </>)}

            <div className="flex items-center justify-between gap-3 pt-2">
              <button onClick={() => setTrin(Math.max(0, trin - 1))} disabled={trin === 0} data-testid="forrige-trin"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-300 bg-white text-slate-700 disabled:opacity-40">
                <ArrowLeft className="h-4 w-4" /> Forrige
              </button>
              {trin < 5 ? (
                <button onClick={() => setTrin(trin + 1)} data-testid="næste-trin"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ backgroundColor: NAVY }}>
                  {trin === 4 ? "Se min analyse" : "Næste trin"} <ArrowRight className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl p-5 text-white" style={{ backgroundColor: NAVY }} data-testid="live-oversigt">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                Live arbejdsoversigt
              </p>
              <p className="mt-3 text-3xl font-bold tabular-nums whitespace-nowrap">{a.arbejdstid.timerOmUgen} timer / uge</p>
              <p className="text-sm text-slate-300">{a.arbejdstid.dageOmUgen} arbejdsdage</p>
              <p className="mt-3 text-xl font-semibold tabular-nums whitespace-nowrap">{a.arbejdstid.timerOmMåneden} timer / måned</p>
              <p className="text-sm text-slate-300">
                heraf {a.arbejdstid.fakturerbareTimerOmMåneden} fakturerbare
              </p>
              <div className="mt-4 h-1.5 rounded-full bg-white/15">
                <div className="h-1.5 rounded-full" style={{ width: `${a.arbejdstid.belægning * 100}%`, backgroundColor: GOLD }} />
              </div>
              <dl className="mt-5 space-y-2 text-sm border-t border-white/10 pt-4">
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Omsætning</dt>
                  <dd className="font-semibold tabular-nums whitespace-nowrap">{kr(a.omsætning.brutto)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Omkostninger</dt>
                  <dd className="font-semibold tabular-nums whitespace-nowrap">{kr(a.omkostninger.total)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Til dig før skat</dt>
                  <dd className="font-semibold tabular-nums whitespace-nowrap" style={{ color: GOLD }}>{kr(a.ejer.førSkat)}</dd></div>
              </dl>
            </div>

            <Card title="Forudsætninger vi regner med" testid="forudsætninger">
              <ul className="space-y-1.5 text-xs text-slate-500">
                <li>Alle beløb er ekskl. moms, fordi momsen ikke er din indtægt.</li>
                <li>{SATSER.arbejdsuger} uger om året minus dine {s.ferieuger} ferieuger.</li>
                <li>AM-bidrag {Math.round(SATSER.amBidrag * 100)} %, din trækprocent {s.trækprocent} %, månedsfradrag {kr(s.månedsfradrag)}.</li>
                <li>Selskabsskat {Math.round(SATSER.selskabsskat * 100)} % ved ApS.</li>
                <li>Chaufførløn får {Math.round((SATSER.feriepengeSats + SATSER.atpOgPension) * 100)} % oveni til feriepenge, ATP og pension.</li>
                <li>Beløb efter skat er et estimat. Din præcise skat afhænger af kommune og fradrag.</li>
              </ul>
            </Card>

            <Card testid="disclaimer">
              <p className="inline-flex items-start gap-2 text-xs text-slate-500">
                <TriangleAlert className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                Beregningerne er vejledende og bygger på dine egne oplysninger. De er ikke en garanti
                for indtjening eller rådgivning om skat.
              </p>
              <Link to="/business" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
                <FileText className="h-3.5 w-3.5" /> Votra Business, drift og økonomi
              </Link>
            </Card>
          </aside>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white" data-testid="onboarding-footer">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
          <span>Spørgsmål til tallene? Ring til Votra på{" "}
            <a href="tel:+4555292828" className="font-semibold" style={{ color: NAVY }}
              data-testid="onboarding-telefon">+45 55 29 28 28</a>
          </span>
          <a href="mailto:kundeservice@votra.dk" className="font-semibold" style={{ color: NAVY }}
            data-testid="onboarding-mail">kundeservice@votra.dk</a>
          <Link to="/kurser" className="hover:underline" data-testid="onboarding-link-kurser">
            Vognmandsuddannelser
          </Link>
          <Link to="/viden/saadan-bliver-du-godsvognmand" className="hover:underline"
            data-testid="onboarding-link-viden">
            Sådan bliver du godsvognmand
          </Link>
          <Link to="/kontakt" className="hover:underline" data-testid="onboarding-link-kontakt">
            Kontakt
          </Link>
        </div>
      </footer>
    </div>
  );
}
